import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/icons';
import { Footer } from '../components/Footer';
import Header from '../components/Header';

const AboutUs: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      {/* Shared Header Component */}
      <Header />

      {/* Content with padding for fixed header */}
      <div className="pt-16">
        {/* Hero section */}
        <div className="relative bg-gradient-to-r from-indigo-700 to-purple-700 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="relative z-10 pb-8  sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
              <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                <div className="sm:text-center lg:text-left">
                  <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                    <span className="block xl:inline">Revolutionizing</span>{' '}
                    <span className="block text-indigo-200 xl:inline">Healthcare with AI</span>
                  </h1>
                  <p className="mt-3 text-base text-indigo-100 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                    Empowering medical professionals worldwide to deliver exceptional care through advanced artificial intelligence solutions.
                  </p>
                </div>
              </main>
            </div>
          </div>
          <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
            <img
              className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2850&q=80"
              alt="Team collaboration"
            />
          </div>
        </div>

        {/* Company overview */}
        <div className="py-16 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">Our Story</h2>
              <p className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
                Transforming Healthcare Globally
              </p>
              <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
                Founded by a team of medical professionals and AI experts, Agilis AI is dedicated to improving patient outcomes through innovative technology.
              </p>
            </div>

            <div className="mt-16">
              <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
                <div>
                  <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                    Our Mission
                  </h3>
                  <p className="mt-3 text-lg text-gray-500">
                    At Agilis AI, we're on a mission to transform healthcare delivery through artificial intelligence. We believe in creating tools that augment medical professionals' capabilities, enabling them to focus more on patient care and less on administrative tasks.
                  </p>

                  <div className="mt-10 space-y-10">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                          <Icon iconName="Lightbulb" size={24} />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium text-gray-900">Innovation-Driven Approach</h4>
                        <p className="mt-2 text-base text-gray-500">
                          We continuously push the boundaries of what's possible in medical AI, investing heavily in research and development.
                        </p>
                      </div>
                    </div>

                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                          <Icon iconName="Security" size={24} />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium text-gray-900">Highest Security Standards</h4>
                        <p className="mt-2 text-base text-gray-500">
                          Patient data security and privacy are paramount in everything we build, exceeding regulatory requirements.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                  <div className="mt-10 lg:mt-0">
                      <div className="pr-4 -ml-48 sm:pr-6 md:-ml-16 lg:px-0 lg:m-0 lg:relative lg:h-full">
                      <img
                          className="rounded-xl shadow-xl ring-1 ring-black ring-opacity-5"
                          src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2850&q=80"
                          alt="Team collaboration"
                      />
                      </div>
                  </div>
              </div>
            </div>
          </div>
        </div>

        {/* Global presence */}
        <div className="bg-gray-50 py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">Global Presence</h2>
              <p className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
                Serving Clients Worldwide
              </p>
            </div>

            <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Toronto Headquarters</h3>
                  <div className="mt-5">
                    <p className="text-base text-gray-500">
                      Our North American headquarters houses our research and development team, focusing on cutting-edge AI algorithms and medical knowledge integration.
                    </p>
                    <div className="mt-4 flex items-center text-sm text-gray-500">
                      <Icon iconName="LocationOn" size={20} className="text-indigo-500 mr-1" />
                      <span>123 Innovation Drive, Toronto, ON, Canada</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Hong Kong Office</h3>
                  <div className="mt-5">
                    <p className="text-base text-gray-500">
                      Our Asian headquarters coordinates our operations in the Asia-Pacific region, with a focus on client relationships and regional adaptation of our solutions.
                    </p>
                    <div className="mt-4 flex items-center text-sm text-gray-500">
                      <Icon iconName="LocationOn" size={20} className="text-indigo-500 mr-1" />
                      <span>456 Tech Tower, Central District, Hong Kong</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Our culture */}
        <div className="bg-white py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
              <div>
                <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                  At Agilis, you thrive
                </h2>
                <p className="mt-3 max-w-3xl text-lg text-gray-500">
                  At Agilis AI, we foster a culture that celebrates diversity, encourages innovation, and prioritizes work-life balance. Our team members come from all corners of the globe, bringing unique perspectives that enrich our solutions.
                </p>
                <div className="mt-8 space-y-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                        <Icon iconName="Diversity3" size={24} />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Diverse & Inclusive</h3>
                      <p className="mt-2 text-base text-gray-500">
                        We believe diverse teams build better products. Our inclusive workplace welcomes talent from all backgrounds.
                      </p>
                    </div>
                  </div>

                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-indigo-500 text-white">
                        <Icon iconName="Psychology" size={24} />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Innovation Mindset</h3>
                      <p className="mt-2 text-base text-gray-500">
                        We encourage creative thinking and provide the freedom to explore new ideas and approaches.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-10 lg:mt-0">
                <img
                  className="rounded-xl shadow-xl ring-1 ring-black ring-opacity-5"
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1778&q=80"
                  alt="Team collaboration"
                />
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-indigo-700">
          <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              <span className="block">Ready to transform healthcare?</span>
              <span className="block">Join our team today.</span>
            </h2>
            <p className="mt-4 text-lg leading-6 text-indigo-200">
              We're always looking for talented individuals who share our vision and values.
            </p>
            <Link
              to="/join-us"
              className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 sm:w-auto"
            >
              View Open Positions
            </Link>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default AboutUs; 